// Day Pass Success — public confirmation page after Stripe checkout completes.
// Public route, no Firebase Auth required. Reads ?session_id=cs_xxx and calls
// the public getDayPassConfirmation CF to fetch safe display fields.
//
// The CF retries internally for ~4s in case the webhook hasn't fired yet.
// We additionally poll up to 5 times at 2s intervals from the client side
// in case the webhook is unusually slow (rare).

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Box, Button, CircularProgress, Stack, Typography, Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';

const gold = '#C9A84C';
const dark = '#0D1E35';
const goldMid = 'rgba(201,168,76,0.6)';

export default function DayPassSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [loading, setLoading] = useState(true);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Day Pass Active · Torch Beach Volleyball';
    let cancelled = false;
    (async () => {
      if (!sessionId) {
        setLoading(false);
        return;
      }

      const functions = getFunctions();
      const getConfirmation = httpsCallable(functions, 'getDayPassConfirmation');

      // Client-side polling: up to 5 attempts, 2s apart. The CF itself
      // retries internally for ~4s, so this covers slow webhook scenarios
      // up to about ~14 seconds total wait.
      const maxAttempts = 5;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        if (cancelled) return;
        try {
          const result = await getConfirmation({ sessionId });
          if (result.data?.found) {
            if (!cancelled) {
              setConfirmation(result.data);
              setLoading(false);
            }
            return;
          }
        } catch (e) {
          console.error('Day pass confirmation lookup failed:', e);
        }
        if (attempt < maxAttempts - 1) {
          await new Promise(r => setTimeout(r, 2000));
        }
      }

      if (!cancelled) {
        setError('We received your payment, but the confirmation is still processing. Your day pass is active — check your email for the receipt.');
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  function formatExpiry() {
    if (!confirmation?.expiresAt) return null;
    const d = new Date(confirmation.expiresAt);
    // Usually today, but a pass bought after closing runs to the next open
    // day, so the day is shown as well as the time.
    const day = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${day} · ${time}`;
  }

  if (loading) {
    return (
      <Box sx={{ background: dark, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
        <CircularProgress sx={{ color: gold }} />
        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
          Confirming your day pass...
        </Typography>
      </Box>
    );
  }

  const expiry = formatExpiry();
  const firstName = confirmation?.firstName;
  const isFullAuth = confirmation?.isFullAuth;

  return (
    <Box sx={{
      background: dark, minHeight: '100vh', color: 'white',
      fontFamily: '"Barlow", sans-serif',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '36px 16px 56px',
    }}>
      {/* Success header */}
      <Box sx={{ textAlign: 'center', mb: 4, mt: { xs: 2, sm: 6 } }}>
        <CheckCircleIcon sx={{ fontSize: 72, color: gold, mb: 2 }} />
        <Box sx={{
          fontFamily: '"Bebas Neue", sans-serif',
          fontSize: 'clamp(2.2rem,6vw,3.2rem)',
          letterSpacing: '4px', lineHeight: 1, mb: 1,
        }}>
          Day Pass Active
        </Box>
        <Box sx={{
          width: 60, height: 2, mx: 'auto', mt: 2,
          background: `linear-gradient(90deg, transparent, ${gold}, transparent)`,
        }} />
      </Box>

      {/* Main card */}
      <Box sx={{
        width: '100%', maxWidth: 480,
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(201,168,76,0.2)',
        borderRadius: '14px',
        p: { xs: 3, sm: 4 },
      }}>
        {error && <Alert severity="warning" sx={{ mb: 3 }}>{error}</Alert>}

        <Stack spacing={2.5}>
          {firstName && (
            <Typography sx={{ fontSize: '1.05rem', textAlign: 'center', color: 'rgba(255,255,255,0.9)' }}>
              Welcome, <strong style={{ color: gold }}>{firstName}</strong>!
            </Typography>
          )}

          <Typography sx={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.75)', textAlign: 'center', lineHeight: 1.6 }}>
            Your day pass is now active. Come on out and play — we've already added you to today's roster.
          </Typography>

          {confirmation?.pin && (
            <Box sx={{
              textAlign: 'center', py: 2,
              background: 'rgba(201,168,76,0.14)',
              border: '1px solid rgba(201,168,76,0.45)',
              borderRadius: '8px',
            }}>
              <Typography sx={{
                fontSize: '0.72rem', color: goldMid,
                letterSpacing: '2px', textTransform: 'uppercase', mb: 0.5,
              }}>
                Your gate PIN
              </Typography>
              <Typography sx={{
                fontFamily: '"Bebas Neue", sans-serif',
                fontSize: '2.6rem', color: gold, lineHeight: 1, letterSpacing: '6px',
              }}>
                {confirmation.pin}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: goldMid, mt: 1, lineHeight: 1.45 }}>
                Enter it on the keypad at the gate. It works until your pass expires
                and is then removed.{confirmation.pinEmailed ? " We've emailed it to you too." : ''}
              </Typography>
            </Box>
          )}

          {expiry && (
            <Box sx={{
              textAlign: 'center', py: 2,
              background: 'rgba(201,168,76,0.08)',
              border: '1px solid rgba(201,168,76,0.25)',
              borderRadius: '8px',
            }}>
              <Typography sx={{
                fontSize: '0.72rem', color: goldMid,
                letterSpacing: '2px', textTransform: 'uppercase', mb: 0.5,
              }}>
                Good for one day — expires
              </Typography>
              <Typography sx={{
                fontFamily: '"Bebas Neue", sans-serif',
                fontSize: '2rem', color: gold, lineHeight: 1,
              }}>
                {expiry}
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: goldMid, mt: 1, lineHeight: 1.45 }}>
                Good for one visit, valid until closing on the date above.
                No refunds for unused passes.
              </Typography>
            </Box>
          )}

          {confirmation?.amountPaid > 0 && (
            <Stack direction="row" justifyContent="space-between" sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
              <span>Amount paid</span>
              <strong style={{ color: 'white' }}>${confirmation.amountPaid.toFixed(2)}</strong>
            </Stack>
          )}

          <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center' }}>
            Your receipt has been emailed to you by Stripe.
          </Typography>
        </Stack>
      </Box>

      {/* CTAs */}
      <Stack spacing={1.5} sx={{ mt: 3, width: '100%', maxWidth: 480 }}>
        {confirmation && !isFullAuth && (
          <Button
            variant="contained" startIcon={<PersonAddIcon />}
            onClick={() => navigate('/register')}
            sx={{
              background: gold, color: dark, fontWeight: 700, py: 1.4,
              '&:hover': { background: '#e0be6a' },
            }}
          >
            Create a full account [optional]
          </Button>
        )}

        <Button
          variant="text"
          onClick={() => navigate('/')}
          sx={{ color: 'rgba(255,255,255,0.6)' }}
        >
          Back to home
        </Button>
      </Stack>

      <Typography sx={{ mt: 4, fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center', maxWidth: 480 }}>
        Questions? Email{' '}
        <Box component="a" href="mailto:info@torchvolleyball.com"
             sx={{ color: goldMid, textDecoration: 'none', '&:hover': { color: gold } }}>
          info@torchvolleyball.com
        </Box>
      </Typography>
    </Box>
  );
}
